/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/page-heading/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import type { PageHeadingElement } from 'BomdisoftComponents/page-heading/render';
import type { ReactNode } from 'react';

export type { PageHeadingElement } from 'BomdisoftComponents/page-heading/render';

export type PageHeadingProps = {
  as?: PageHeadingElement;
  children: ReactNode;
  className?: string;
  testId?: string;
};

/**
 * The title a console page wears, and the one heading a page should have.
 *
 * It exists because the same thing had happened here that produced
 * `SectionHeading`: fifteen screens wrote this by hand and drifted into two
 * spellings of one design. Twelve rendered `h2` with a local `.pageTitle`, three
 * rendered `h1` with a local `.title`, and every one of them resolved to the
 * same 20px, weight 650, -0.02em. Nothing looked wrong, which is exactly why it
 * lasted: one heading is only visibly wrong beside a different one, and these
 * were never on screen together.
 *
 * **`h1` is the default because the `h2` spelling was a real defect, not a
 * preference.** The console shell renders no heading of its own, so a page
 * titled `h2` left the document with no `h1` at all: measured on `/dashboards`,
 * `document.querySelectorAll('h1').length` was 0, and a reader navigating by
 * heading level arrived at a page whose outline began at level two with nothing
 * above it. The three `h1` screens were the correct ones.
 *
 * Nothing about the appearance changes. This is the same type the fifteen were
 * already rendering, in one place, so the sixteenth cannot invent a third
 * spelling.
 */
export const PageHeading = ({ as: Element = 'h1', children, className, testId }: PageHeadingProps) => (
  <Element className={joinClasses('bomdisoft-page-heading', className)} data-testid={testId}>
    {children}
  </Element>
);
